import { useParams } from 'react-router-dom';
import type { GetEndorsementsResponse } from '@responses/endorsements';
import { queryOptions, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { getRequestAuthPayload } from '../helpers/getRequestAuth';

import type { QueryFactory } from './factory.types';
import type { UseQueryRefetchFn, UseQueryStatus } from './types';

const getUrl = (submissionId: string) => [`submissions`, submissionId, `endorsements`].join('/');

export const fetchEndorsements = async (submissionId: string): Promise<GetEndorsementsResponse> => {
  const networkPayload = await getRequestAuthPayload({
    method: 'get',
    url: getUrl(submissionId),
  });

  const response = await axios<GetEndorsementsResponse>(networkPayload);
  return response.data;
};

export const endorsementQueries = {
  forSubmission: (submissionId: string) =>
    queryOptions({
      queryKey: ['endorsements', submissionId] as const,
      queryFn: () => fetchEndorsements(submissionId),
      staleTime: 0,
    }),
} satisfies QueryFactory;

interface EndorsementDataHook {
  data: GetEndorsementsResponse | undefined;
  status: UseQueryStatus;
  isFetching: boolean;
  refetch: UseQueryRefetchFn<any, any>;
}

export const useEndorsementData = (): EndorsementDataHook => {
  const { id = '' } = useParams<{ id: string }>();
  const { data, status, isFetching, refetch } = useQuery(endorsementQueries.forSubmission(id));
  return { data, status, isFetching, refetch };
};
